/**
 * Risk matrix #2 — change RISK derived from impact × likelihood (Step 49).
 *
 * The same pattern as `priorityMatrix`, and deliberately a SECOND matrix
 * rather than a generalised one: two matrices with different axes, different
 * sizes and different outputs share nothing but the idea, and the abstraction
 * that unified them would have to be configured at every call site to say
 * which of the two it meant.
 *
 * Shared for the same reason as the first: the browser previews the derived
 * risk while the change is being planned, and the server enforces it on write.
 * One function, two runtimes, zero drift.
 *
 * ── The axes ───────────────────────────────────────────────────────────
 *
 * **Impact C1–C4** is how much breaks if this goes wrong — C1 the whole
 * organisation, C4 one person. Note the direction: C1 is the WORST, which is
 * the convention every change board writing "it's a C1" already uses, and
 * inverting it to make the numbers ascend would be technically tidier and
 * wrong in every meeting.
 *
 * **Likelihood 1–4** is how likely it is to go wrong — 1 almost certain,
 * 4 very unlikely. Same direction as impact, on purpose: two axes that count
 * opposite ways is how somebody eventually reads the grid backwards.
 */
import type { ApprovalState } from './approvals.ts';

export const CHANGE_IMPACTS = ['C1', 'C2', 'C3', 'C4'] as const;
export const CHANGE_LIKELIHOODS = ['1', '2', '3', '4'] as const;
export type ChangeImpact = (typeof CHANGE_IMPACTS)[number];
export type ChangeLikelihood = (typeof CHANGE_LIKELIHOODS)[number];
export type ChangeRisk = 'low' | 'medium' | 'high' | 'critical';

/**
 * Rows are impact (C1 worst → C4 least), columns likelihood (1 most likely →
 * 4 least). Written out in full rather than computed from a score, because a
 * change board argues about individual cells and an arithmetic rule cannot be
 * argued with — this can simply be edited.
 */
const RISK: Record<ChangeImpact, Record<ChangeLikelihood, ChangeRisk>> = {
  C1: { '1': 'critical', '2': 'critical', '3': 'high', '4': 'medium' },
  C2: { '1': 'critical', '2': 'high', '3': 'medium', '4': 'medium' },
  C3: { '1': 'high', '2': 'medium', '3': 'medium', '4': 'low' },
  C4: { '1': 'medium', '2': 'medium', '3': 'low', '4': 'low' },
};

export function deriveChangeRisk(impact: ChangeImpact, likelihood: ChangeLikelihood): ChangeRisk {
  return RISK[impact][likelihood];
}

export const CHANGE_IMPACT_HELP =
  'How much breaks if this goes wrong. C1 is the whole organisation; C4 is one person.';
export const CHANGE_LIKELIHOOD_HELP =
  'How likely it is to go wrong. 1 is almost certain; 4 is very unlikely.';

export const CHANGE_IMPACT_LABEL: Record<ChangeImpact, string> = {
  C1: 'C1 — organisation-wide',
  C2: 'C2 — a whole service or site',
  C3: 'C3 — a team',
  C4: 'C4 — one or two people',
};
export const CHANGE_LIKELIHOOD_LABEL: Record<ChangeLikelihood, string> = {
  '1': '1 — almost certain',
  '2': '2 — likely',
  '3': '3 — unlikely',
  '4': '4 — very unlikely',
};

/**
 * The three kinds of change, and what each one means for the process.
 *
 * `standard` is the interesting one: pre-approved means the approval gate is
 * SKIPPED, not auto-passed — there is no approval record because nobody was
 * asked, and manufacturing one would put a decision in the audit trail that
 * no human ever made.
 */
export const CHANGE_KINDS = ['normal', 'standard', 'emergency'] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];

export const CHANGE_KIND_LABEL: Record<ChangeKind, string> = {
  normal: 'Normal',
  standard: 'Standard (pre-approved)',
  emergency: 'Emergency',
};
export const CHANGE_KIND_HELP: Record<ChangeKind, string> = {
  normal: 'The default. Planned, reviewed, and approved before it goes ahead.',
  standard: 'A routine, pre-approved change. Skips the approval gate entirely — nobody is asked.',
  emergency: 'Fix now, review after. Still needs approval, but the planning gate is relaxed.',
};

/** The change lifecycle, in order. `cancelled` is reachable from anywhere and
 *  is therefore not part of the sequence. */
export const CHANGE_STAGES = [
  'open',
  'planning',
  'awaiting_approval',
  'pending_release',
  'pending_review',
  'closed',
] as const;
export type ChangeStage = (typeof CHANGE_STAGES)[number];

/**
 * How the change went, asked at review. Three values rather than free text,
 * because "what proportion of our changes succeed?" is the one metric change
 * management exists to produce, and prose cannot be counted.
 */
export const CHANGE_OUTCOMES = ['successful', 'with_issues', 'unsuccessful'] as const;
export type ChangeOutcome = (typeof CHANGE_OUTCOMES)[number];

export const CHANGE_OUTCOME_LABEL: Record<ChangeOutcome, string> = {
  successful: 'Completed successfully',
  with_issues: 'Completed with issues',
  unsuccessful: 'Unsuccessful',
};
export const CHANGE_OUTCOME_HELP: Record<ChangeOutcome, string> = {
  successful: 'It did what it set out to do, in the window, with nothing unexpected.',
  with_issues: 'It landed, but something went wrong on the way — overran, needed a fix, caused a blip.',
  unsuccessful: 'It did not land, or it was backed out.',
};

/**
 * Which outcomes owe an explanation — Step 198.
 *
 * Kai: *"if completed with issues, what issues"* and *"if unsuccessful,
 * why"*. `successful` needs nothing; the other two are the only ones anybody
 * ever reads a change record to understand, and recording them as a bare word
 * throws away the whole value of having asked. "Completed with issues" with
 * no issues written down is indistinguishable from a mis-click.
 */
export function outcomeNeedsNote(outcome: string | null | undefined): boolean {
  return outcome === 'with_issues' || outcome === 'unsuccessful';
}

/** What to ask for, per outcome. Empty when nothing is owed. */
export const CHANGE_OUTCOME_NOTE_PROMPT: Record<ChangeOutcome, string> = {
  successful: '',
  with_issues: 'What went wrong? What was the impact, and what did you have to do about it?',
  unsuccessful: 'Why did it not land? Was it backed out, and is anything left behind to tidy up?',
};

export const CHANGE_OUTCOME_NOTE_LABEL: Record<ChangeOutcome, string> = {
  successful: 'Notes',
  with_issues: 'What issues?',
  unsuccessful: 'Why?',
};

export interface ChangeGateInput {
  kind: ChangeKind;
  justification: string | null;
  backoutPlan: string | null;
  testPlan: string | null;
  /**
   * Where this change stands on approval — see `approvalState`.
   *
   * Step 193. This was a boolean, and a boolean cannot distinguish "refused"
   * from "nobody was ever asked". A change with no CAB therefore read as
   * unapproved and could never leave Awaiting approval.
   */
  approval: ApprovalState;
  /**
   * The name of a board that COVERS this change and insists on a decision —
   * Step 195. Null when no board applies, or the one that does is advisory.
   *
   * Step 193 made "nobody was asked" a pass, which is right for a desk with no
   * CAB and wrong for one that configured a board and meant it. This is what
   * tells the two apart, and it is a NAME rather than a boolean so the refusal
   * can say which board is waiting — "needs approval" with no clue who from is
   * the dead end this whole area started as.
   */
  mandatoryBoard?: string | null | undefined;
  /** How it went. Required to CLOSE — see `changeGate`. */
  outcome?: string | null | undefined;
  /** What issues / why it failed. Required alongside the two outcomes that
   *  imply there is something to explain — Step 198. */
  outcomeNote?: string | null | undefined;
}

/**
 * May a change move from `from` to `to`? Returns null when it may, or the
 * reason when it may not.
 *
 * The two gates the step names, and one rule that falls out of them:
 *
 * 1. **Leaving Planning needs a justification, a backout plan and a test
 *    plan.** Relaxed for an emergency — the whole point of an emergency
 *    change is that the building is on fire, and a process that will not let
 *    you act until the paperwork is done is a process people route around.
 *    It still has to be written up; the gate simply is not where it is
 *    enforced.
 * 2. **Leaving Awaiting Approval needs an actual approval.** Skipped for a
 *    standard change, because pre-approved means nobody is asked — see
 *    `CHANGE_KIND_HELP`.
 *
 * Moving BACKWARDS is always allowed: a change that turns out to need more
 * planning must be able to go back, and a lifecycle you can only walk forwards
 * is one people abandon and re-raise.
 */
export function changeGate(from: ChangeStage, to: ChangeStage, c: ChangeGateInput): string | null {
  if (from === to) return null;
  /**
   * Closing needs an OUTCOME. Kai: *"as a change is completed it needs a
   * completed successfully, with issues or unsuccessful"*.
   *
   * This is the one gate that applies to an emergency and a standard change
   * too. Skipping the paperwork to ACT quickly is reasonable; skipping the
   * record of whether it worked is how a change process stops being able to
   * answer the only question anybody asks of it.
   */
  if (to === 'closed') {
    if (!c.outcome) return 'Say how the change went before closing it.';
    /**
     * Step 198 — and say WHAT, when the outcome implies there is something to
     * say. A change recorded as "completed with issues" and nothing else is a
     * worse record than one marked successful, because it advertises that
     * something happened and then refuses to say what.
     */
    if (outcomeNeedsNote(c.outcome) && !c.outcomeNote?.trim())
      return c.outcome === 'unsuccessful'
        ? 'Say why it was unsuccessful before closing it.'
        : 'Say what the issues were before closing it.';
    return null;
  }
  const iFrom = CHANGE_STAGES.indexOf(from);
  const iTo = CHANGE_STAGES.indexOf(to);
  if (iTo < iFrom) return null; // backwards is always allowed

  if (from === 'planning' && c.kind !== 'emergency') {
    const missing = [
      !c.justification?.trim() && 'a justification',
      !c.backoutPlan?.trim() && 'a backout plan',
      !c.testPlan?.trim() && 'a test plan',
    ].filter(Boolean) as string[];
    if (missing.length) return `A change needs ${missing.join(', ')} before it leaves Planning.`;
  }

  /**
   * Leaving Awaiting approval.
   *
   * `none` passes. If no approval was ever requested there is nothing to wait
   * for, and blocking on it strands the change with no action that would ever
   * release it — approval is opt-in per change, raised from the Approvals tab.
   * A standard change is pre-authorised and skips this entirely.
   */
  if (from === 'awaiting_approval' && c.kind !== 'standard') {
    if (c.approval === 'pending')
      return 'An approval is still open on this change — it needs a decision first.';
    if (c.approval === 'rejected')
      return 'This change was refused. Send it back to Planning to revise it, then ask again.';
    // Nobody was asked, and a board says somebody has to be. Only reachable
    // when that board does not raise itself — otherwise entering the stage
    // would have created the chain, and `approval` would read `pending`.
    if (c.approval === 'none' && c.mandatoryBoard)
      return `${c.mandatoryBoard} has to approve this change. Raise it from the Approvals tab.`;
  }

  return null;
}

/**
 * Risk as a COMPARABLE scale — Step 195.
 *
 * `deriveChangeRisk` has always returned one of four words, and nothing could
 * ask whether one was worse than another. Board applicability is the first
 * thing that needs to ("this CAB applies at high and above"), so the ordering
 * becomes explicit rather than implied by the order of a union type.
 */
export const RISK_ORDER: readonly ChangeRisk[] = ['low', 'medium', 'high', 'critical'];

/**
 * Is `risk` at least as severe as `floor`?
 *
 * **An unassessed change counts as the WORST.** A change with no impact or
 * likelihood recorded has not been judged safe, it has not been judged at all,
 * and the two are only the same thing to a system that wants an easy answer.
 * Treating `null` as `low` would route exactly the changes nobody thought
 * about past exactly the boards meant to catch them, so it goes the other way:
 * unassessed collects the most approval, not the least. The cure is to fill
 * the risk matrix in, which takes two clicks and is the point.
 */
export function riskAtLeast(risk: string | null | undefined, floor: string | null | undefined): boolean {
  if (!floor) return true; // the board does not care about risk
  if (!risk) return true; // unassessed — see above
  const a = RISK_ORDER.indexOf(risk as ChangeRisk);
  const b = RISK_ORDER.indexOf(floor as ChangeRisk);
  if (a === -1 || b === -1) return true; // unknown words fail SAFE, not open
  return a >= b;
}

/** What a board is being matched against. */
export interface BoardMatchContext {
  ticketType: string;
  kind: string | null;
  risk: string | null;
  groupId: string | null;
}

/** The applicability rules a board carries. Empty/absent means "do not care". */
export interface BoardRules {
  id: string;
  name: string;
  isActive?: boolean | number | undefined;
  appliesToTypes?: string[] | null | undefined;
  appliesToKinds?: string[] | null | undefined;
  appliesToGroupIds?: string[] | null | undefined;
  minRisk?: string | null | undefined;
  autoRaise?: boolean | number | undefined;
  priority?: number | null | undefined;
}

const has = (list: string[] | null | undefined, v: string | null): boolean =>
  !list || list.length === 0 ? true : v !== null && list.includes(v);

/** Does this board cover this change? */
export function boardMatches(b: BoardRules, ctx: BoardMatchContext): boolean {
  if (b.isActive === 0 || b.isActive === false) return false;
  return (
    has(b.appliesToTypes, ctx.ticketType) &&
    has(b.appliesToKinds, ctx.kind) &&
    has(b.appliesToGroupIds, ctx.groupId) &&
    riskAtLeast(ctx.risk, b.minRisk ?? null)
  );
}

/** How many constraints a board states. More = more specific. */
function specificity(b: BoardRules): number {
  return (
    (b.appliesToTypes?.length ? 1 : 0) +
    (b.appliesToKinds?.length ? 1 : 0) +
    (b.appliesToGroupIds?.length ? 1 : 0) +
    (b.minRisk ? 1 : 0)
  );
}

/**
 * Pick the ONE board that applies.
 *
 * Most specific wins, then the higher `priority`, then the name — the last so
 * that two identically-configured boards resolve the same way on every run
 * rather than however the database felt like ordering them.
 *
 * One board, not several. A group CAB and an org-wide high-risk CAB will both
 * match a risky Infrastructure change, and running both would mean the change
 * needs two independent chains to agree — which is a real thing some
 * organisations want, and is NOT what "most specific wins" gives you. Stacking
 * is deliberately left out until somebody asks for it, because the failure
 * mode of guessing is that a board somebody configured is silently skipped.
 */
export function pickBoard<T extends BoardRules>(boards: readonly T[], ctx: BoardMatchContext): T | null {
  const matching = boards.filter((b) => boardMatches(b, ctx));
  if (!matching.length) return null;
  return [...matching].sort(
    (x, y) =>
      specificity(y) - specificity(x) ||
      (y.priority ?? 0) - (x.priority ?? 0) ||
      x.name.localeCompare(y.name),
  )[0]!;
}

/** Does this STAGE run at this risk? A stage with no floor always runs. */
export function stageApplies(stage: { minRisk?: string | null | undefined }, risk: string | null): boolean {
  return riskAtLeast(risk, stage.minRisk ?? null);
}
